import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded'
import { useNavigate } from 'react-router-dom'
import { adminService, type FormWritePayload } from '../../services/adminService'
import type { EventForm, FormField } from '../../data/types'
import { EmptyState, LoadingState } from '../../components/ui/States'
import { apiError } from '../errors'
import { useToast } from '../useToast'
import { confirmDelete } from '../confirm'
import TargetingSelect from './TargetingSelect'

const FORM_TYPES = ['rsvp', 'survey', 'feedback', 'meal', 'qa', 'meeting_request']
const STATUSES = ['open', 'closed']
const FIELD_TYPES = ['text', 'textarea', 'select', 'radio', 'checkbox', 'rating']

export default function FormsManager({ eventId }: { eventId: string }) {
  const { notify } = useToast()
  const navigate = useNavigate()
  const [forms, setForms] = useState<EventForm[]>([])
  const [counts, setCounts] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<EventForm | null>(null)
  const [open, setOpen] = useState(false)

  const load = () =>
    adminService
      .listForms(eventId)
      .then(async (fs) => {
        setForms(fs)
        // Fetch response counts (best-effort).
        const entries = await Promise.all(
          fs.map(async (f) => {
            try {
              const r = await adminService.listResponses(eventId, f.id)
              return [f.id, r.total] as const
            } catch {
              return [f.id, 0] as const
            }
          }),
        )
        setCounts(Object.fromEntries(entries))
      })
      .catch((e) => notify(apiError(e), 'error'))
      .finally(() => setLoading(false))

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId])

  const remove = async (f: EventForm) => {
    if (!(await confirmDelete(`Delete form "${f.title}"?`))) return
    try {
      await adminService.deleteForm(eventId, f.id)
      notify('Form deleted')
      load()
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }

  if (loading) return <LoadingState />

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddRoundedIcon />}
          onClick={() => {
            setEditing(null)
            setOpen(true)
          }}
        >
          Add form
        </Button>
      </Stack>

      {forms.length === 0 ? (
        <EmptyState title="No forms yet" description="Create RSVPs, surveys, and feedback forms." />
      ) : (
        <Stack spacing={1.5}>
          {forms.map((f) => (
            <Card
              key={f.id}
              variant="outlined"
              sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, borderRadius: 3 }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle1" fontWeight={700} noWrap>
                    {f.title}
                  </Typography>
                  <Chip
                    label={f.status}
                    size="small"
                    sx={{ height: 18, fontSize: '0.65rem', textTransform: 'capitalize' }}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {f.fields.length} field{f.fields.length === 1 ? '' : 's'} ·{' '}
                  {counts[f.id] ?? 0} response{counts[f.id] === 1 ? '' : 's'}
                </Typography>
              </Box>
              <Button
                size="small"
                startIcon={<BarChartRoundedIcon />}
                onClick={() =>
                  navigate(`/admin/events/${eventId}/forms/${f.id}/responses`)
                }
                sx={{ whiteSpace: 'nowrap' }}
              >
                Responses
              </Button>
              <IconButton
                size="small"
                onClick={() => {
                  setEditing(f)
                  setOpen(true)
                }}
              >
                <EditRoundedIcon />
              </IconButton>
              <IconButton size="small" color="error" onClick={() => remove(f)}>
                <DeleteRoundedIcon />
              </IconButton>
            </Card>
          ))}
        </Stack>
      )}

      {open && (
        <FormDialog
          eventId={eventId}
          form={editing}
          onClose={() => setOpen(false)}
          onSaved={() => {
            setOpen(false)
            load()
          }}
        />
      )}
    </Box>
  )
}

interface EditableField {
  label: string
  type: string
  required: boolean
  options: string
}

function toEditable(fields: FormField[]): EditableField[] {
  return fields.map((f) => ({
    label: f.label,
    type: f.type,
    required: Boolean(f.required),
    options: (f.options ?? []).join('\n'),
  }))
}

function FormDialog({
  eventId,
  form,
  onClose,
  onSaved,
}: {
  eventId: string
  form: EventForm | null
  onClose: () => void
  onSaved: () => void
}) {
  const { notify } = useToast()
  const [saving, setSaving] = useState(false)
  const [meta, setMeta] = useState({
    title: form?.title ?? '',
    description: form?.description ?? '',
    type: form?.type ?? 'survey',
    status: form?.status ?? 'open',
    dueLabel: form?.dueLabel ?? '',
  })
  const [targetingRuleId, setTargetingRuleId] = useState<string | null>(
    form?.targetingRuleId ?? null,
  )
  const [fields, setFields] = useState<EditableField[]>(
    form ? toEditable(form.fields) : [{ label: '', type: 'text', required: false, options: '' }],
  )
  const setMetaField = (k: keyof typeof meta, v: string) =>
    setMeta((m) => ({ ...m, [k]: v }))

  const updateField = (idx: number, patch: Partial<EditableField>) =>
    setFields((fs) => fs.map((f, i) => (i === idx ? { ...f, ...patch } : f)))
  const addField = () =>
    setFields((fs) => [...fs, { label: '', type: 'text', required: false, options: '' }])
  const removeField = (idx: number) => setFields((fs) => fs.filter((_, i) => i !== idx))

  const save = async () => {
    if (!meta.title) {
      notify('Title is required', 'error')
      return
    }
    const cleaned = fields.filter((f) => f.label.trim())
    if (cleaned.length === 0) {
      notify('Add at least one field', 'error')
      return
    }
    const payload: FormWritePayload = {
      title: meta.title,
      description: meta.description || undefined,
      type: meta.type,
      status: meta.status,
      dueLabel: meta.dueLabel || undefined,
      targetingRuleId,
      fields: cleaned.map((f) => ({
        label: f.label,
        type: f.type,
        required: f.required,
        options:
          f.type === 'select' || f.type === 'radio'
            ? f.options
                .split('\n')
                .map((o) => o.trim())
                .filter(Boolean)
            : undefined,
      })),
    }
    setSaving(true)
    try {
      if (form) await adminService.updateForm(eventId, form.id, payload)
      else await adminService.createForm(eventId, payload)
      notify(form ? 'Form updated' : 'Form created')
      onSaved()
    } catch (e) {
      notify(apiError(e), 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{form ? 'Edit form' : 'Add form'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Title"
            required
            fullWidth
            value={meta.title}
            onChange={(e) => setMetaField('title', e.target.value)}
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            minRows={2}
            value={meta.description}
            onChange={(e) => setMetaField('description', e.target.value)}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Type"
              select
              fullWidth
              value={meta.type}
              onChange={(e) => setMetaField('type', e.target.value)}
            >
              {FORM_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Status"
              select
              fullWidth
              value={meta.status}
              onChange={(e) => setMetaField('status', e.target.value)}
            >
              {STATUSES.map((s) => (
                <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <TextField
            label="Due label"
            fullWidth
            placeholder="Due before Day 1"
            value={meta.dueLabel}
            onChange={(e) => setMetaField('dueLabel', e.target.value)}
          />
          <TargetingSelect
            eventId={eventId}
            value={targetingRuleId}
            onChange={setTargetingRuleId}
          />

          <Divider>
            <Typography variant="overline" color="text.secondary">
              Fields
            </Typography>
          </Divider>

          {fields.map((f, idx) => (
            <Card key={idx} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    label="Field label"
                    fullWidth
                    size="small"
                    value={f.label}
                    onChange={(e) => updateField(idx, { label: e.target.value })}
                  />
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => removeField(idx)}
                    disabled={fields.length === 1}
                  >
                    <DeleteRoundedIcon fontSize="small" />
                  </IconButton>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    label="Type"
                    select
                    size="small"
                    sx={{ minWidth: 130 }}
                    value={f.type}
                    onChange={(e) => updateField(idx, { type: e.target.value })}
                  >
                    {FIELD_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>
                        {t}
                      </MenuItem>
                    ))}
                  </TextField>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={f.required}
                        onChange={(e) => updateField(idx, { required: e.target.checked })}
                      />
                    }
                    label="Required"
                  />
                </Stack>
                {(f.type === 'select' || f.type === 'radio') && (
                  <TextField
                    label="Options (one per line)"
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    value={f.options}
                    onChange={(e) => updateField(idx, { options: e.target.value })}
                  />
                )}
              </Stack>
            </Card>
          ))}

          <Button startIcon={<AddRoundedIcon />} onClick={addField} sx={{ alignSelf: 'flex-start' }}>
            Add field
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
